"use client";

import React, { useState } from "react";
import { Check, ChevronRight, ChevronLeft, Wrench, MapPin, Phone, Mail, User, MessageSquare, ShieldCheck, Clock, Award, Zap, Power, Lightbulb, ClipboardCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
const services = [{
  id: "Panel Upgrades",
  label: "Panel Upgrades",
  icon: Zap
}, {
  id: "24/7 Service",
  label: "24/7 Service",
  icon: Clock
}, {
  id: "EV Chargers",
  label: "EV Chargers",
  icon: Zap
}, {
  id: "Generators",
  label: "Generators",
  icon: Power
}, {
  id: "Lighting",
  label: "Lighting Upgrades",
  icon: Lightbulb
}, {
  id: "Surge Protection",
  label: "Surge Protection",
  icon: ShieldCheck
}, {
  id: "Repairs",
  label: "Electrical Repairs",
  icon: Wrench
}, {
  id: "Inspections",
  label: "Inspections",
  icon: ClipboardCheck
}];
const cities = ["Richmond", "Sugar Land", "Missouri City", "Houston", "Katy", "Rosenberg"];
function ServiceRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const nextStep = () => setStep(prev => Math.min(prev + 1, 2));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in duration-500">
                <div className="h-20 w-20 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                    <Check className="h-10 w-10" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-secondary">Inquiry Received</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">{successMessage}</p>
                </div>
                <Button variant="outline" onClick={() => window.location.reload()} className="border-primary text-primary hover:bg-primary/5 rounded-lg">
                    Submit Another Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
            <div className="relative flex items-center justify-between mb-12 px-12">
                <div className="absolute top-5 left-12 right-12 h-0.5 bg-muted -z-0 hidden md:block" />
                {[1, 2].map((s, i) => <div key={s} data-index={i} className="flex flex-col items-center relative z-10">
                        <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold transition-all duration-300 border-2", step >= s ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-muted")}>
                            {step > s ? <Check className="h-5 w-5" /> : s}
                        </div>
                        <span className={cn("text-xs mt-2 font-mono uppercase tracking-wider font-bold transition-colors duration-300", step >= s ? "text-primary" : "text-muted-foreground")}>
                            {s === 1 ? "Service" : "Details"}
                        </span>
                    </div>)}
            </div>

            {step === 1 && <div className="space-y-6 animate-in slide-in-from-right-8 duration-300">
                    <div className="text-center space-y-2">
                        <h3 className="text-xl font-bold text-secondary uppercase tracking-tight">Select Electrical Service</h3>
                        <p className="text-sm text-muted-foreground font-sans">Choose the primary technical service required for your project.</p>
                    </div>

                    <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
                                <FormControl>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {services.map((service, i) => {
              const Icon = service.icon;
              const isSelected = field.value === service.id;
              return <div key={service.id} data-index={i} onClick={() => field.onChange(service.id)} className={cn("cursor-pointer rounded-lg border-2 p-3 flex flex-col items-center justify-center gap-2 text-center transition-all hover:border-primary/50 hover:bg-muted/30", isSelected ? "border-primary bg-primary/5 text-primary shadow-md" : "border-muted bg-background text-muted-foreground")}>
                                                    <div className={cn("p-2.5 rounded-lg transition-colors", isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                                                        <Icon className="h-5 w-5" />
                                                    </div>
                                                    <span className="text-[10px] font-bold uppercase font-mono leading-tight">{service.label}</span>
                                                </div>;
            })}
                                    </div>
                                </FormControl>
                                <FormMessage className="text-center mt-4 font-mono text-xs" />
                            </FormItem>} />
                </div>}

            {step === 2 && <div className="space-y-4 animate-in slide-in-from-right-8 duration-300">
                    <div className="text-center space-y-2 mb-6">
                        <h3 className="text-xl font-bold text-secondary uppercase tracking-tight">Contact Information</h3>
                        <p className="text-sm text-muted-foreground font-sans">Provide your details for a rapid response from our Richmond team.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField control={control} name="name" rules={{
          required: "Name is required"
        }} render={({
          field
        }) => <FormItem>
                                    <FormLabel className="font-mono text-xs font-bold uppercase">Full Name</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                                            <Input placeholder="John Humberg" className="pl-9 rounded-lg" {...field} />
                                        </div>
                                    </FormControl>
                                    <FormMessage className="font-mono text-xs" />
                                </FormItem>} />

                        <FormField control={control} name="phone" rules={{
          required: "Phone is required"
        }} render={({
          field
        }) => <FormItem>
                                    <FormLabel className="font-mono text-xs font-bold uppercase">Phone Number</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                                            <Input type="tel" placeholder="(832) 405-3766" className="pl-9 rounded-lg" {...field} />
                                        </div>
                                    </FormControl>
                                    <FormMessage className="font-mono text-xs" />
                                </FormItem>} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^\S+@\S+$/i,
            message: "Invalid email"
          }
        }} render={({
          field
        }) => <FormItem>
                                    <FormLabel className="font-mono text-xs font-bold uppercase">Email Address</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                                            <Input type="email" placeholder="admin@humbergelectric.com" className="pl-9 rounded-lg" {...field} />
                                        </div>
                                    </FormControl>
                                    <FormMessage className="font-mono text-xs" />
                                </FormItem>} />

                        <FormField control={control} name="city" rules={{
          required: "City is required"
        }} render={({
          field
        }) => <FormItem>
                                    <FormLabel className="font-mono text-xs font-bold uppercase">Service Area</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="rounded-lg w-full">
                                                <SelectValue placeholder="Select your location" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {cities.map((city, i) => <SelectItem key={city} value={city} data-index={i}>{city}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage className="font-mono text-xs" />
                                </FormItem>} />
                    </div>

                    <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
                                <FormLabel className="font-mono text-xs font-bold uppercase">Subject</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                                        <Input className="pl-9 rounded-lg" {...field} value={field.value || ""} />
                                    </div>
                                </FormControl>
                                <FormMessage className="font-mono text-xs" />
                            </FormItem>} />

                    <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
                                <FormLabel className="font-mono text-xs font-bold uppercase">Inquiry Details</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                                        <Textarea placeholder="Please describe your electrical project or repair needs..." className="pl-9 min-h-[100px] rounded-lg" {...field} value={field.value || ""} />
                                    </div>
                                </FormControl>
                                <FormMessage className="font-mono text-xs" />
                            </FormItem>} />
                </div>}

            <div className="flex justify-between pt-4 border-t border-border">
                <Button type="button" variant="ghost" onClick={prevStep} disabled={step === 1 || isSubmitting} className={cn(step === 1 && "invisible", "font-mono text-xs uppercase font-bold rounded-lg")}>
                    <ChevronLeft className="mr-2 h-4 w-4" /> Previous
                </Button>

                {step < 2 ? <Button type="button" onClick={nextStep} className="bg-primary hover:bg-primary/90 rounded-lg font-mono text-xs uppercase font-bold">
                        Continue <ChevronRight className="ml-2 h-4 w-4" />
                    </Button> : <Button type="submit" variant="destructive" disabled={isSubmitting} className="min-w-[140px] rounded-lg font-mono text-xs uppercase font-bold w-full md:w-auto">
                        {isSubmitting ? "Processing..." : "Submit Inquiry"}
                    </Button>}
            </div>

            {submitError && <div className="p-3 bg-destructive/10 text-destructive text-xs font-mono rounded-lg text-center">
                    {submitError}
                </div>}
        </Form>;
}
export default function RequestCoastalService() {
  return <section id="new-customer-inquiry" className="py-16 md:py-24 bg-background text-foreground relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute bottom-[10%] left-[5%] w-[30%] h-[30%] bg-destructive/5 rounded-full blur-3xl" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        
                        <div className="lg:col-span-2">
                            <Card className="border-t-4 border-t-primary shadow-2xl bg-card/95 backdrop-blur-sm h-full rounded-lg">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-2xl md:text-3xl font-bold text-primary uppercase tracking-tighter">
                                        New Customer Inquiry
                                    </CardTitle>
                                    <CardDescription className="text-base font-sans">
                                        Connect with Humberg Electrical Services for professional residential and commercial support.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    <FormProvider formId="new_customer_inquiry_contact" sectionName="new-customer-inquiry" defaultValues={{
                  service_type: "",
                  name: "",
                  phone: "",
                  email: "",
                  city: "",
                  subject: "NEW CUSTOMER INQUIRY",
                  message: ""
                }} validationRules={{
                  service_type: {
                    required: true
                  },
                  name: {
                    required: true
                  },
                  phone: {
                    required: true,
                    pattern: "phone"
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  city: {
                    required: true
                  }
                }} submitText="Submit Inquiry" successMessage="Thank you! Your inquiry has been received. Our team will contact you shortly to discuss your electrical needs.">
                                        <ServiceRequestForm />
                                    </FormProvider>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="lg:col-span-1 space-y-6">
                            <div className="bg-secondary text-secondary-foreground rounded-lg p-8 shadow-xl h-full flex flex-col justify-between relative overflow-hidden">
                                <div className="absolute inset-0 opacity-5 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
                                
                                <div className="relative z-10">
                                    <h3 className="text-2xl font-bold mb-6 font-sans uppercase tracking-tight border-b border-white/10 pb-4">Why Choose Humberg?</h3>
                                    
                                    <ul className="space-y-6">
                                        <li className="flex items-start gap-4">
                                            <div className="bg-primary/20 p-2 rounded-lg shrink-0">
                                                <Clock className="h-6 w-6 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-lg font-sans">24/7 Availability</h4>
                                                <p className="text-secondary-foreground/70 text-sm leading-relaxed">
                                                    We provide around-the-clock emergency service calls for Richmond and surrounding areas.
                                                </p>
                                            </div>
                                        </li>
                                        
                                        <li className="flex items-start gap-4">
                                            <div className="bg-destructive/20 p-2 rounded-lg shrink-0">
                                                <ShieldCheck className="h-6 w-6 text-destructive" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-lg font-sans">Family Operated</h4>
                                                <p className="text-secondary-foreground/70 text-sm leading-relaxed">
                                                    Since 2013, providing reliable service where no job is too big or too small.
                                                </p>
                                            </div>
                                        </li>
                                        
                                        <li className="flex items-start gap-4">
                                            <div className="bg-primary/20 p-2 rounded-lg shrink-0">
                                                <Award className="h-6 w-6 text-primary" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-lg font-sans">Licensed Experts</h4>
                                                <p className="text-secondary-foreground/70 text-sm leading-relaxed">
                                                    Master electrical technicians ensuring code compliance and high-safety standards.
                                                </p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>

                                <div className="mt-8 pt-8 border-t border-white/10 relative z-10">
                                    <p className="text-xs font-mono uppercase tracking-widest text-secondary-foreground/50 mb-2">Direct Support</p>
                                    <Link to="tel:8324053766" className="text-2xl font-bold text-white hover:text-primary transition-colors flex items-center gap-2">
                                        <Phone className="h-5 w-5" />
                                        (832) 405-3766
                                    </Link>
                                    <p className="text-xs font-mono text-secondary-foreground/50 mt-4">admin@humbergelectric.com</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>;
}